import Phaser from 'phaser';
import { TILE, PlayerId } from '../types';
import { fitStaticBodyToTile } from '../systems/SpriteScaling';

export const EXPLOSION_MS = 450;

export type ExplosionOrientation = 'center' | 'horizontal' | 'vertical';

export class ExplosionTile extends Phaser.Physics.Arcade.Sprite {
  readonly col: number;
  readonly row: number;
  readonly ownerId: PlayerId;

  constructor(scene: Phaser.Scene, col: number, row: number, orientation: ExplosionOrientation, ownerId: PlayerId) {
    const x = col * TILE + TILE / 2;
    const y = row * TILE + TILE / 2;
    super(scene, x, y, orientation === 'center' ? 'explosion-center' : 'explosion-arm');
    this.col = col;
    this.row = row;
    this.ownerId = ownerId;
    scene.add.existing(this);

    // Stretch the arm along its axis so neighbouring tiles overlap visually.
    // The hand-drawn flame textures are symmetric, so we set displaySize
    // directly without worrying about asymmetric AI imagery.
    const longSide = TILE * 1.6;
    const shortSide = TILE * 1.1;
    if (orientation === 'center') {
      this.setDisplaySize(shortSide, shortSide);
    } else {
      // Arm texture is naturally wide; setAngle(90) swaps visual axes for vertical.
      this.setDisplaySize(longSide, shortSide);
    }
    scene.physics.add.existing(this, true);
    const body = this.body as Phaser.Physics.Arcade.StaticBody;
    body.setSize(TILE - 6, TILE - 6);
    body.setOffset(0, 0);
    this.refreshBody();

    if (orientation === 'vertical') this.setAngle(90);
    this.setDepth(500);

    // Capture per-axis scales (they differ because the texture isn't square)
    const baseX = this.scaleX;
    const baseY = this.scaleY;
    this.setScale(baseX * 0.3, baseY * 0.3);
    this.setAlpha(1);
    scene.tweens.add({
      targets: this,
      scaleX: { from: baseX * 0.3, to: baseX * 1.12 },
      scaleY: { from: baseY * 0.3, to: baseY * 1.12 },
      duration: 130,
      ease: 'Back.easeOut',
    });
    // Flicker pulse mid-burn
    scene.tweens.add({
      targets: this,
      scaleX: { from: baseX * 1.12, to: baseX * 0.95 },
      scaleY: { from: baseY * 1.12, to: baseY * 0.95 },
      delay: 140,
      duration: 90,
      yoyo: true,
      repeat: 1,
    });
    scene.tweens.add({
      targets: this,
      alpha: 0,
      delay: 180,
      duration: EXPLOSION_MS - 180,
      onComplete: () => this.destroy(),
    });
  }
}
